import { jsPDF } from 'jspdf';
import { addLogoToPdfHeader } from './pdfLogoHelper';
import * as BABYLON from '@babylonjs/core';
import { calculateLux, getLuxRating, getProjectionArea, DEFAULT_EFFICIENCY, ASPECT_RATIOS as LUX_ASPECT_RATIOS } from '../../utils/luxCalculation';
import { LuxHeatmapSystem } from './LuxHeatmapSystem';

// ==================== CONSTANTS ====================
const PX_PER_METER = 300;
const VOLTAGE = 220;
const ASPECT_RATIOS = {
  '16:9': { value: 16 / 9 }, '16:10': { value: 16 / 10 }, '4:3': { value: 4 / 3 },
  '21:9': { value: 21 / 9 }, '1:1': { value: 1 }, '2.35:1': { value: 2.35 }
};

const VIEWS_6 = [
  { name: 'Front',  alpha: -Math.PI / 2, beta: Math.PI / 2 },
  { name: 'Back',   alpha: Math.PI / 2,  beta: Math.PI / 2 },
  { name: 'Left',   alpha: Math.PI,      beta: Math.PI / 2 },
  { name: 'Right',  alpha: 0,            beta: Math.PI / 2 },
  { name: 'Top',    alpha: -Math.PI / 2, beta: 0.01 },
  { name: 'Bottom', alpha: -Math.PI / 2, beta: Math.PI - 0.01 },
];

function luxRatingForReport(lux, projectType) {
  return getLuxRating(lux, projectType);
}

function estimatePowerW(lumens) {
  return Math.round((lumens || 10000) / 10);
}

// Stacking multiplier: 1 + top + bottom + left + right (when enabled).
// Used to scale effective lumens for lux/brightness calculations.
function getStackCount(p) {
  const s = p?.stacking;
  if (!s || !s.enabled) return 1;
  return 1 + (parseInt(s.top) || 0) + (parseInt(s.bottom) || 0) + (parseInt(s.left) || 0) + (parseInt(s.right) || 0);
}

function effectiveLumens(p) {
  return (p?.lumens || 10000) * getStackCount(p);
}

// ==================== SURFACE DETECTION ====================
function assignProjectorsToSurfaces(projectors, sceneObjects) {
  const surfaces = sceneObjects.filter(o =>
    !['projector','reverse_projector','truss_system','scaffolding','led_screen','mirror'].includes(o.type)
  );
  const surfaceMap = {};
  surfaces.forEach(s => { surfaceMap[s.id] = { surface: s, projectors: [] }; });
  surfaceMap['__unassigned__'] = { surface: { name: 'General / Unassigned', id: '__unassigned__', dimensions: {} }, projectors: [] };

  projectors.forEach(proj => {
    const pos = proj.position || { x: 0, y: 0, z: 0 };
    let bestId = null, bestDist = Infinity;
    surfaces.forEach(s => {
      const sp = s.position || { x: 0, y: 0, z: 0 };
      const d = Math.sqrt(Math.pow(pos.x - sp.x, 2) + Math.pow(pos.y - sp.y, 2) + Math.pow(pos.z - sp.z, 2));
      if (d < (proj.throw_distance || 5) * 2.5 && d < bestDist) { bestDist = d; bestId = s.id; }
    });
    (surfaceMap[bestId] || surfaceMap['__unassigned__']).projectors.push(proj);
  });

  return Object.values(surfaceMap).filter(s => s.projectors.length > 0);
}

// ==================== CAMERA CAPTURE ====================
function captureFromCamera(scene, engine, camState, w, h) {
  return new Promise((resolve) => {
    const cam = new BABYLON.ArcRotateCamera('rptCam', camState.alpha, camState.beta, camState.radius,
      new BABYLON.Vector3(camState.target.x, camState.target.y, camState.target.z), scene);
    cam.fov = camState.fov || 0.8; cam.minZ = 0.1; cam.maxZ = 1000;
    const prev = scene.activeCamera;
    scene.activeCamera = cam;
    let f = 0;
    const obs = scene.onAfterRenderObservable.add(() => {
      f++;
      if (f >= 3) {
        scene.onAfterRenderObservable.remove(obs);
        BABYLON.Tools.CreateScreenshotUsingRenderTarget(engine, cam, { width: w, height: h }, (data) => {
          scene.activeCamera = prev; cam.dispose(); resolve(data);
        });
      }
    });
  });
}

// ==================== SCENE BOUNDS (from actual rendered Babylon meshes) ====================
// Walks every visible / enabled mesh in the scene and unions their world-space
// AABBs. This gives us the EXACT extent of what the user sees on screen — every
// surface, projector, truss, scaffolding, beam, mount and decorative item is
// included with its real rendered geometry. Far better than relying on user-set
// `dimensions` data which often under-reports actual mesh size.
function computeSceneBoundsFromMeshes(scene) {
  if (!scene) return null;
  // Skip helpers and non-geometry meshes — these are not part of the show.
  const SKIP_NAME_PATTERNS = [
    /^ground$/i, /^grid/i, /grid_/i, /gizmo/i, /^bg_/i, /helper/i,
    /^pointerDragPlane$/i, /^orthoCam_/i, /^rptCam$/i, /label/i, /tag/i,
    /^debug/i, /^_/i, /^dimension/i, /^snap/i, /highlight/i
  ];
  const isHelper = (name = '') => SKIP_NAME_PATTERNS.some(re => re.test(name));

  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;

  for (const mesh of scene.meshes) {
    if (!mesh) continue;
    if (mesh.isDisposed && mesh.isDisposed()) continue;
    if (mesh._isMesh === false) continue;
    if (!mesh.isVisible || !mesh.isEnabled()) continue;
    if (isHelper(mesh.name)) continue;
    // Skip meshes with zero geometry (e.g. abstract transform nodes used as parents)
    const totalVerts = mesh.getTotalVertices ? mesh.getTotalVertices() : 0;
    if (!totalVerts) continue;

    try {
      mesh.computeWorldMatrix(true);
      const bi = mesh.getBoundingInfo();
      if (!bi) continue;
      const bb = bi.boundingBox;
      // Babylon caches min/max in world-space when computeWorldMatrix is fresh
      const wmin = bb.minimumWorld, wmax = bb.maximumWorld;
      if (!wmin || !wmax) continue;
      if (!isFinite(wmin.x) || !isFinite(wmax.x)) continue;
      minX = Math.min(minX, wmin.x); maxX = Math.max(maxX, wmax.x);
      minY = Math.min(minY, wmin.y); maxY = Math.max(maxY, wmax.y);
      minZ = Math.min(minZ, wmin.z); maxZ = Math.max(maxZ, wmax.z);
    } catch (e) { /* ignore stray mesh */ }
  }
  if (!isFinite(minX)) return null;

  // Always pull the floor of the frame to ground level so building bases
  // aren't lifted off the bottom of the view.
  minY = Math.min(minY, 0);

  const center = new BABYLON.Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
  const extX = Math.max(maxX - minX, 0.1), extY = Math.max(maxY - minY, 0.1), extZ = Math.max(maxZ - minZ, 0.1);
  const diagonal = Math.sqrt(extX * extX + extY * extY + extZ * extZ);
  return { minX, maxX, minY, maxY, minZ, maxZ, center, extX, extY, extZ, diagonal };
}

// Legacy entry point kept so older call sites compile — delegates to the
// mesh-based implementation when a scene is available, otherwise computes a
// fallback from `sceneObjects` data only.
function computeSceneBoundsFromObjects(sceneObjects, _projectors = [], scene = null) {
  if (scene) {
    const fromMesh = computeSceneBoundsFromMeshes(scene);
    if (fromMesh) return fromMesh;
  }
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;
  for (const obj of (sceneObjects || [])) {
    const p = obj.position || { x: 0, y: 0, z: 0 };
    const d = obj.dimensions || { width: 1, height: 1, depth: 1 };
    const hw = (d.width || 1) / 2, hh = (d.height || 1) / 2, hd = (d.depth || 1) / 2;
    minX = Math.min(minX, p.x - hw); maxX = Math.max(maxX, p.x + hw);
    minY = Math.min(minY, p.y - hh); maxY = Math.max(maxY, p.y + hh);
    minZ = Math.min(minZ, p.z - hd); maxZ = Math.max(maxZ, p.z + hd);
  }
  if (!isFinite(minX)) return null;
  minY = Math.min(minY, 0);
  const center = new BABYLON.Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
  const extX = Math.max(maxX - minX, 0.1), extY = Math.max(maxY - minY, 0.1), extZ = Math.max(maxZ - minZ, 0.1);
  const diagonal = Math.sqrt(extX * extX + extY * extY + extZ * extZ);
  return { minX, maxX, minY, maxY, minZ, maxZ, center, extX, extY, extZ, diagonal };
}

// Hide ground + grid meshes, return list to restore
function hideGrid(scene) {
  const hidden = [];
  for (const m of scene.meshes) {
    if ((m.name === 'ground' || m.name === 'grid') && (m.isVisible || m.isEnabled())) {
      if (m.isVisible) { m.isVisible = false; hidden.push({ mesh: m, prop: 'isVisible' }); }
    }
  }
  return hidden;
}
function showGrid(hidden) {
  for (const h of hidden) h.mesh[h.prop] = true;
}

// Convert PNG data URL to JPEG for smaller file size
function toJpeg(dataUrl, quality) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width; c.height = img.height;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#1a1a2e'; ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL('image/jpeg', quality || 0.65));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ==================== ORTHO SCENE CAPTURE ====================
function captureOrthoScene(scene, engine, view, bounds, baseW, bgColor) {
  return new Promise((resolve) => {
    const savedClear = scene.clearColor ? scene.clearColor.clone() : null;
    const hiddenGridItems = hideGrid(scene);
    if (bgColor) scene.clearColor = bgColor;

    // Wider margin (50% total / 25% per side) so projectors, mounts, truss
    // tops and any object near the edge of the bounding box never get cropped.
    const margin = 1.5;
    let hExt, vExt;
    if (view.name === 'Top' || view.name === 'Bottom') { hExt = bounds.extX; vExt = bounds.extZ; }
    else if (view.name === 'Left' || view.name === 'Right') { hExt = bounds.extZ; vExt = bounds.extY; }
    else { hExt = bounds.extX; vExt = bounds.extY; }
    hExt = Math.max(hExt, 0.5); vExt = Math.max(vExt, 0.5);

    // Capture at correct aspect ratio
    const aspect = vExt / hExt;
    const capW = baseW;
    const capH = Math.max(Math.round(baseW * aspect), 100);

    const cam = new BABYLON.ArcRotateCamera('orthoCam_' + view.name,
      view.alpha, view.beta, bounds.diagonal * 3, bounds.center.clone(), scene);
    cam.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;
    cam.orthoLeft = -(hExt * margin) / 2;
    cam.orthoRight = (hExt * margin) / 2;
    cam.orthoTop = (vExt * margin) / 2;
    cam.orthoBottom = -(vExt * margin) / 2;
    cam.minZ = 0.01;
    cam.maxZ = bounds.diagonal * 20;

    const prev = scene.activeCamera;
    scene.activeCamera = cam;

    let f = 0;
    const obs = scene.onAfterRenderObservable.add(() => {
      if (++f >= 4) {
        scene.onAfterRenderObservable.remove(obs);
        BABYLON.Tools.CreateScreenshotUsingRenderTarget(engine, cam, { width: capW, height: capH }, (data) => {
          scene.activeCamera = prev;
          cam.dispose();
          showGrid(hiddenGridItems);
          if (bgColor) scene.clearColor = savedClear || new BABYLON.Color4(0.05, 0.05, 0.1, 1);
          resolve({ data, aspect });
        });
      }
    });
  });
}

// ==================== ORTHO LINE DRAWING (white bg, projectors colored, correct aspect) ====================
function captureLineDrawing(scene, engine, view, bounds, baseW, projectors) {
  return new Promise((resolve) => {
    const savedClear = scene.clearColor ? scene.clearColor.clone() : null;
    const savedMats = new Map();
    const hiddenGridItems = hideGrid(scene);

    scene.clearColor = new BABYLON.Color4(1, 1, 1, 1);

    // Build projector ID → color map (use body_color for visibility)
    const projColorMap = new Map();
    for (const p of (projectors || [])) {
      projColorMap.set(p.id, p.body_color || p.projection_color || p.color || '#FF0000');
    }

    for (const mesh of scene.meshes) {
      if (!mesh.isEnabled() || !mesh.isVisible) continue;
      if (mesh.name === 'ground' || mesh.name === 'grid') continue;
      if (mesh.material) savedMats.set(mesh, mesh.material);
      const mat = new BABYLON.StandardMaterial('ld_' + mesh.uniqueId, scene);
      mat.wireframe = true;
      mat.disableLighting = true;
      mat.backFaceCulling = false;

      // Detect projector: check self + entire parent chain for metadata.id in projector map
      let projColor = null;
      let node = mesh;
      while (node && !projColor) {
        const mid = node.metadata?.id;
        if (mid && projColorMap.has(mid)) projColor = projColorMap.get(mid);
        // Also match by name containing projector id
        if (!projColor && mid) {
          const nm = (node.name || '').toLowerCase();
          if (nm.includes('projector') || nm.includes('proj') || nm.includes('beam') || nm.includes('frustum') || nm.includes('lens')) {
            for (const [pid, col] of projColorMap) {
              if (nm.includes(pid) || mid === pid) { projColor = col; break; }
            }
          }
        }
        node = node.parent;
      }

      if (projColor) {
        try { mat.emissiveColor = BABYLON.Color3.FromHexString(projColor); }
        catch { mat.emissiveColor = new BABYLON.Color3(1, 0, 0); }
      } else {
        mat.emissiveColor = new BABYLON.Color3(0.35, 0.35, 0.35);
      }
      mesh.material = mat;
    }

    // Wider margin (50% total / 25% per side) so projectors, mounts, truss
    // tops and any object near the edge of the bounding box never get cropped.
    const margin = 1.5;
    let hExt, vExt;
    if (view.name === 'Top' || view.name === 'Bottom') { hExt = bounds.extX; vExt = bounds.extZ; }
    else if (view.name === 'Left' || view.name === 'Right') { hExt = bounds.extZ; vExt = bounds.extY; }
    else { hExt = bounds.extX; vExt = bounds.extY; }
    hExt = Math.max(hExt, 0.5); vExt = Math.max(vExt, 0.5);

    const aspect = vExt / hExt;
    const capW = baseW;
    const capH = Math.max(Math.round(baseW * aspect), 100);

    const cam = new BABYLON.ArcRotateCamera('ldCam_' + view.name,
      view.alpha, view.beta, bounds.diagonal * 3, bounds.center.clone(), scene);
    cam.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;
    cam.orthoLeft = -(hExt * margin) / 2;
    cam.orthoRight = (hExt * margin) / 2;
    cam.orthoTop = (vExt * margin) / 2;
    cam.orthoBottom = -(vExt * margin) / 2;
    cam.minZ = 0.01;
    cam.maxZ = bounds.diagonal * 20;

    const prev = scene.activeCamera;
    scene.activeCamera = cam;

    let f = 0;
    const obs = scene.onAfterRenderObservable.add(() => {
      if (++f >= 4) {
        scene.onAfterRenderObservable.remove(obs);
        BABYLON.Tools.CreateScreenshotUsingRenderTarget(engine, cam, { width: capW, height: capH }, (data) => {
          scene.activeCamera = prev;
          cam.dispose();
          scene.clearColor = savedClear || new BABYLON.Color4(0.05, 0.05, 0.1, 1);
          showGrid(hiddenGridItems);
          for (const mesh2 of scene.meshes) {
            if (mesh2.material && mesh2.material.name?.startsWith('ld_')) {
              const tmp = mesh2.material;
              if (savedMats.has(mesh2)) mesh2.material = savedMats.get(mesh2);
              else mesh2.material = null;
              tmp.dispose();
            }
          }
          resolve({ data, aspect });
        });
      }
    });
  });
}

// ==================== MAIN EXPORT ====================
export async function exportProjectionReport({
  projectors, sceneObjects, savedCameras, overlapAnalysis, calculateOverlaps,
  projectName, userName, projectSettings, scene, engine
}) {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pw = 210, ph = 297, m = 12;
  const cw = pw - m * 2;
  let y = m;
  const projType = projectSettings?.projectType || 'indoor';

  const toDeg = (r) => ((r || 0) * 180 / Math.PI).toFixed(1);

  // ============ HELPERS ============
  const hdr = (text, sz) => {
    checkPage(14);
    pdf.setFontSize(sz || 11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(text, m, y);
    y += 4.5;
    pdf.setDrawColor(180, 50, 180);
    pdf.setLineWidth(0.5);
    pdf.line(m, y, pw - m, y);
    y += 4;
  };

  const row = (label, value, col) => {
    checkPage(6);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(90, 90, 90);
    pdf.text(label, m + 4, y);
    if (col) pdf.setTextColor(...col);
    else pdf.setTextColor(30, 30, 30);
    pdf.setFont('helvetica', 'bold');
    pdf.text(String(value ?? '—'), m + 80, y);
    y += 4.5;
  };

  const row2col = (l1, v1, l2, v2) => {
    checkPage(6);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(90, 90, 90);
    pdf.text(l1, m + 4, y);
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30);
    pdf.text(String(v1 ?? '—'), m + 42, y);
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90, 90, 90);
    pdf.text(l2, m + 96, y);
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30);
    pdf.text(String(v2 ?? '—'), m + 138, y);
    y += 4.5;
  };

  const sep = (s) => { y += (s || 2); };

  const checkPage = (needed) => {
    if (y + (needed || 10) > ph - m) { pdf.addPage(); y = m; }
  };

  const drawTable = (headers, rows, colWidths, startX) => {
    const x0 = startX || m;
    const totalW = colWidths.reduce((a, b) => a + b, 0);
    const rowH = 4.8;

    checkPage(rowH * 2 + 4);
    pdf.setFillColor(30, 30, 45);
    pdf.rect(x0, y, totalW, rowH, 'F');
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    let cx = x0 + 1.5;
    headers.forEach((h, i) => { pdf.text(h, cx, y + 3.3); cx += colWidths[i]; });
    y += rowH;

    rows.forEach((r, ri) => {
      checkPage(rowH + 2);
      if (ri % 2 === 0) { pdf.setFillColor(246, 244, 249); pdf.rect(x0, y, totalW, rowH, 'F'); }
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(40, 40, 40);
      cx = x0 + 1.5;
      r.forEach((cell, ci) => {
        if (cell && typeof cell === 'object' && cell._color) {
          pdf.setTextColor(...cell._color);
          pdf.setFont('helvetica', 'bold');
          pdf.text(String(cell.text || ''), cx, y + 3.3);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(40, 40, 40);
        } else {
          pdf.text(String(cell ?? '—').substring(0, Math.floor(colWidths[ci] / 2)), cx, y + 3.3);
        }
        cx += colWidths[ci];
      });
      y += rowH;
    });
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.15);
    pdf.line(x0, y, x0 + totalW, y);
    y += 2;
  };

  const drawLuxBar = (lux, rating, barW) => {
    checkPage(7);
    const fill = Math.min(lux / 15000, 1);
    pdf.setFillColor(230, 230, 230);
    pdf.roundedRect(m + 4, y, barW, 3.5, 1, 1, 'F');
    pdf.setFillColor(...rating.color);
    if (fill > 0.02) pdf.roundedRect(m + 4, y, barW * fill, 3.5, 1, 1, 'F');
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...rating.color);
    pdf.text(`${Math.round(lux)} lux — ${rating.label}`, m + barW + 8, y + 2.8);
    y += 5.5;
  };

  // ============ PAGE 1: COVER ============
  pdf.setFillColor(18, 24, 38);
  pdf.rect(0, 0, pw, 40, 'F');
  pdf.setFillColor(200, 50, 200);
  pdf.rect(0, 40, pw, 1.5, 'F');
  // AV Simulation Tool logo (top-right of cover header)
  await addLogoToPdfHeader(pdf, { x: pw - m - 30, y: 5, height: 18 });
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Projection Report', m, 18);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(180, 200, 220);
  pdf.text('Combined Technical Report — Surface Based', m, 28);
  pdf.setFontSize(7);
  pdf.text('Generated by AV Simulation Tool', m, 35);
  pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }), pw - m - 55, 35);
  y = 48;

  // Project info
  pdf.setFontSize(8);
  pdf.setTextColor(90, 90, 90);
  pdf.text('Project:', m, y);
  pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30);
  pdf.text(projectName || 'Untitled', m + 18, y);
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90, 90, 90);
  pdf.text('Prepared by:', m + 80, y);
  pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30);
  pdf.text(userName || 'System User', m + 105, y);
  y += 5;
  row('Total Projectors', `${projectors.length}`);
  sep(3);

  // ============ PROJECTOR OVERVIEW (GRID) ============
  hdr('Projector Overview');
  const ovHeaders = ['#', 'Name', 'Model', 'Lumens', 'Resolution', 'Throw Ratio', 'Throw Dist', 'Lens'];
  const ovWidths = [7, 22, 30, 20, 24, 20, 20, 43];
  const ovRows = projectors.map((p, i) => [
    `${i + 1}`,
    (p.name || '').substring(0, 12),
    (p.model || '').substring(0, 18),
    `${(p.lumens || 10000).toLocaleString()}`,
    `${p.image_width}x${p.image_height}`,
    `${(p.throw_ratio || 1.5).toFixed(2)}`,
    `${(p.throw_distance || 5).toFixed(1)}m`,
    (p.selected_lens?.name || 'Default').substring(0, 24)
  ]);
  drawTable(ovHeaders, ovRows, ovWidths);
  sep(4);

  // ============ SURFACE-BASED SECTIONS ============
  const surfaceGroups = assignProjectorsToSurfaces(projectors, sceneObjects);

  for (const group of surfaceGroups) {
    const surf = group.surface;
    const projs = group.projectors;
    const dims = surf.dimensions || { width: 1, height: 1, depth: 1 };

    pdf.addPage();
    y = m;

    // Surface banner
    pdf.setFillColor(30, 30, 45);
    pdf.rect(0, 0, pw, 18, 'F');
    pdf.setFillColor(200, 50, 200);
    pdf.rect(0, 18, pw, 1, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    const surfName = surf.name || surf.type || 'Surface';
    pdf.text(`Surface: ${surfName}`, m, 12);
    if (dims.width) {
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(180, 200, 220);
      pdf.text(`${dims.width?.toFixed(1)} x ${dims.height?.toFixed(1)} x ${(dims.depth || 0).toFixed(1)} m  |  ${projs.length} Projector(s)`, pw - m - 60, 12);
    }
    y = 24;

    // ---- Per-Projector Detail in this surface ----
    for (let pi = 0; pi < projs.length; pi++) {
      const p = projs[pi];
      const pos = p.position || { x: 0, y: 0, z: 0 };
      const rot = p.rotation || { x: 0, y: 0, z: 0 };
      const ratio = ASPECT_RATIOS[p.aspect_ratio] || ASPECT_RATIOS['16:10'];
      const td = p.throw_distance || 5;
      const tr = p.throw_ratio || 1.5;
      const imgWidthM = td / tr;
      const imgHeightM = imgWidthM / ratio.value;
      const imgArea = imgWidthM * imgHeightM;
      const _stackN = getStackCount(p);
      const _effLumens = effectiveLumens(p);
      const lux = calculateLux(_effLumens, imgArea);
      const rating = luxRatingForReport(lux, projType);
      const ppiH = Math.round((p.image_height || 1200) / imgHeightM);
      const ppiW = Math.round((p.image_width || 1920) / imgWidthM);
      const projAngle = pos.y > 0 ? Math.atan2(pos.y, td) * 180 / Math.PI : 0;
      const bodyW = p.weight || p.spec?.weight || 20;
      const frameW = p.show_frame ? (p.frame_weight || p.spec?.frame_weight || 0) : 0;
      const clampW = p.show_clamp ? (p.clamp_weight || 2) : 0;
      const totalWt = bodyW + frameW + clampW;
      const powerW = (p.spec?.power || estimatePowerW(p.lumens)) * _stackN;

      // Projector sub-header
      checkPage(90);
      pdf.setFillColor(245, 240, 252);
      pdf.rect(m, y, cw, 6, 'F');
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(80, 40, 120);
      pdf.text(`${p.name || p.model || 'Projector'} — ${p.model || ''}`, m + 3, y + 4.2);
      y += 8;

      // === PROJECTOR SUMMARY (Grid: 2 columns) ===
      row2col('Model', p.model || 'Unknown', 'Brand', p.spec?.brand || '—');
      row2col('Resolution', `${p.image_width} x ${p.image_height} px`, 'Brightness', `${(p.lumens || 10000).toLocaleString()} lm`);
      row2col('Aspect Ratio', p.aspect_ratio || '16:10', 'Lens', p.selected_lens?.name || 'Default');
      const lensType = p.lensType === 'lens90'
        ? 'Ultra-wide (90\u00B0)'
        : (p.lensType === 'topBottom' ? 'Periscope'
          : (p.lensType === 'ust' ? 'UST Lens (rear-throw)' : 'Standard'));
      row2col('Lens Type', lensType, 'Throw Ratio', `${tr.toFixed(2)}`);
      if (p.selected_lens) {
        row2col('Throw Range', `${p.selected_lens.throw_ratio_min} – ${p.selected_lens.throw_ratio_max}`, '', '');
      }
      if (p.lensType === 'lens90') {
        row('Lens 90\u00B0 Orientation', p.lens90Rotation || 'right');
      }
      if (p.lensType === 'topBottom') {
        const dir = p.topBottomDirection === 'bottom' ? 'Bottom mount (lens below body)' : 'Top mount (lens above body)';
        row('Lens Mount', dir);
      }
      if (p.out_of_range) {
        row('Warning', 'THROW RATIO OUT OF RANGE', [200, 40, 40]);
      }
      sep(2);

      // === POSITION & PLACEMENT ===
      row2col('Position X', `${pos.x.toFixed(2)} m`, 'Position Y', `${pos.y.toFixed(2)} m`);
      row2col('Position Z', `${pos.z.toFixed(2)} m`, 'Throw Distance', `${td.toFixed(2)} m`);
      row2col('Projection Angle', `${projAngle.toFixed(1)}\u00B0`, '', '');
      sep(1);

      // === ROTATION / ORIENTATION ===
      row2col('Pitch (X)', `${toDeg(rot.x)}\u00B0`, 'Yaw (Y)', `${toDeg(rot.y)}\u00B0`);
      row('Roll (Z)', `${toDeg(rot.z)}\u00B0`);
      sep(2);

      // === PROJECTION OUTPUT ===
      row2col('Image Width', `${imgWidthM.toFixed(2)} m`, 'Image Height', `${imgHeightM.toFixed(2)} m`);
      row2col('Image Area', `${imgArea.toFixed(2)} m\u00B2`, 'Resolution', `${p.image_width} x ${p.image_height} px`);
      row2col('Pixel Density (H)', `${ppiH} px/m`, 'Pixel Density (W)', `${ppiW} px/m`);
      sep(2);

      // === LENS SHIFT ===
      row2col('Lens Shift H', `${(p.lens_shift_h || 0).toFixed(1)}%`, 'Lens Shift V', `${(p.lens_shift_v || 0).toFixed(1)}%`);
      sep(2);

      // === LUX ANALYSIS ===
      row2col('Lumens', `${(p.lumens || 10000).toLocaleString()} lm`, 'Efficiency Factor', `${DEFAULT_EFFICIENCY}`);
      if (_stackN > 1) {
        row2col('Stack Count', `×${_stackN} (T${p.stacking?.top || 0} B${p.stacking?.bottom || 0} L${p.stacking?.left || 0} R${p.stacking?.right || 0})`,
                'Effective Lumens', `${_effLumens.toLocaleString()} lm`);
      }
      row2col('Projection Area', `${imgArea.toFixed(2)} m\u00B2`, 'Mode', projType.charAt(0).toUpperCase() + projType.slice(1));
      row2col('Lux Value', `${Math.round(lux)} lx`, 'Rating', '');
      // Rating colored
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...rating.color);
      pdf.text(rating.label, m + 138, y - 4.5);
      drawLuxBar(lux, rating, 100);
      sep(1);

      // === WEIGHT & POWER ===
      row2col('Projector Weight', `${bodyW.toFixed(1)} kg`, 'Frame Weight', frameW > 0 ? `${frameW.toFixed(1)} kg` : '—');
      row2col('Clamp Weight', clampW > 0 ? `${clampW.toFixed(1)} kg` : '—', 'Total Weight', `${totalWt.toFixed(1)} kg`);
      row2col('Power', `${powerW} W (${(powerW / 1000).toFixed(2)} kW)`, 'Current', `${(powerW / VOLTAGE).toFixed(1)} A @ ${VOLTAGE}V`);
      row2col('Body Size', `${p.body_width || 500}x${p.body_height || 200}x${p.body_depth || 600} mm`, '', '');
      sep(3);

      // Divider between projectors in same surface
      if (pi < projs.length - 1) {
        pdf.setDrawColor(200, 200, 210);
        pdf.setLineWidth(0.3);
        pdf.line(m + 10, y, pw - m - 10, y);
        y += 4;
      }
    }

    // === COMBINED LUX SUMMARY for this surface ===
    sep(3);
    checkPage(30);
    hdr(`Lux Summary — ${surfName}`);
    const luxSumHeaders = ['Projector', 'Lumens', 'Area (m\u00B2)', 'Eff.', 'Lux (lx)', 'Rating'];
    const luxSumWidths = [28, 26, 22, 14, 22, 26];
    let combinedLux = 0;
    const luxSumRows = projs.map(p => {
      const lumens = effectiveLumens(p);
      const ratio = ASPECT_RATIOS[p.aspect_ratio] || ASPECT_RATIOS['16:10'];
      const td = p.throw_distance || 5;
      const imgW = td / (p.throw_ratio || 1.5);
      const area = imgW * (imgW / ratio.value);
      const lx = calculateLux(lumens, area);
      combinedLux += lx;
      const r = luxRatingForReport(lx, projType);
      return [
        (p.name || '').substring(0, 16),
        `${lumens.toLocaleString()}`,
        `${area.toFixed(2)}`,
        `${DEFAULT_EFFICIENCY}`,
        `${Math.round(lx)}`,
        { text: r.label, _color: r.color }
      ];
    });
    // Combined row
    const combinedRating = luxRatingForReport(combinedLux, projType);
    luxSumRows.push([
      { text: 'COMBINED', _color: [200, 50, 200] }, '', '', '',
      { text: `${Math.round(combinedLux)} lx`, _color: [200, 50, 200] },
      { text: combinedRating.label, _color: combinedRating.color }
    ]);
    drawTable(luxSumHeaders, luxSumRows, luxSumWidths);
    y += 2;

    // === SURFACE COVERAGE (per-face resolution) ===
    if (surf.id !== '__unassigned__') {
      sep(3);
      checkPage(30);
      hdr(`Surface Coverage — ${surfName}`);
      const objType = (surf.type || '').toLowerCase();
      const hRes = surf.heightResolution || Math.round((dims.height || 1) * PX_PER_METER);
      const ppm = hRes / (dims.height || 1);

      const faces = [];
      if (objType === 'cylinder' || objType === 'pipe') {
        const circ = 2 * Math.PI * (dims.width || 1) / 2;
        faces.push({ name: 'Curved Surface', w: Math.round(circ * ppm), h: hRes });
        const cap = Math.round((dims.width || 1) * ppm);
        faces.push({ name: 'Top Cap', w: cap, h: cap });
        faces.push({ name: 'Bottom Cap', w: cap, h: cap });
      } else if (objType === 'sphere' || objType === 'hemisphere' || objType === 'dome') {
        faces.push({ name: 'Spherical Map', w: Math.round((dims.width || 1) * ppm) * 2, h: hRes });
      } else if (objType === 'plane' || objType === 'projection_screen' || objType === 'scrim_screen') {
        faces.push({ name: 'Front', w: Math.round((dims.width || 1) * ppm), h: hRes });
      } else {
        const wPx = Math.round((dims.width || 1) * ppm);
        const dPx = Math.round((dims.depth || 1) * ppm);
        faces.push({ name: 'Front', w: wPx, h: hRes });
        faces.push({ name: 'Back', w: wPx, h: hRes });
        faces.push({ name: 'Left', w: dPx, h: hRes });
        faces.push({ name: 'Right', w: dPx, h: hRes });
        faces.push({ name: 'Top', w: wPx, h: dPx });
        faces.push({ name: 'Bottom', w: wPx, h: dPx });
      }

      const fHeaders = ['Face', 'Width (px)', 'Height (px)', 'Megapixels'];
      const fWidths = [35, 35, 35, 30];
      const fRows = faces.map(f => [f.name, f.w.toLocaleString(), f.h.toLocaleString(), ((f.w * f.h) / 1000000).toFixed(2)]);
      drawTable(fHeaders, fRows, fWidths);
    }
  }

  // ============ CAMERA VIEWS (2 per row) ============
  if (savedCameras && savedCameras.length > 0 && scene && engine) {
    pdf.addPage();
    y = m;
    hdr('Camera Views');
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(90, 90, 90);
    pdf.text('Scene previews from saved camera positions — for client visualization and approvals.', m + 2, y);
    y += 5;

    const imgW = (cw - 6) / 2;
    const imgH = imgW * (9 / 16);
    let col = 0;

    for (let ci = 0; ci < savedCameras.length; ci++) {
      const cam = savedCameras[ci];
      try {
        const imgData = await captureFromCamera(scene, engine, cam, 1000, 563);
        if (imgData) {
          const jpgData = await toJpeg(imgData, 0.6);
          if (col === 0) checkPage(imgH + 14);
          const xOff = col === 0 ? m : m + imgW + 6;

          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(40, 40, 40);
          pdf.text(cam.name || `Camera ${ci + 1}`, xOff, y);
          const camY = y + 2;
          pdf.addImage(jpgData, 'JPEG', xOff, camY, imgW, imgH);

          pdf.setFontSize(6);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(120, 120, 120);
          const fovDeg = ((cam.fov || 0.8) * 180 / Math.PI).toFixed(0);
          pdf.text(`FOV: ${fovDeg}\u00B0 | Alpha: ${toDeg(cam.alpha)}\u00B0 | Beta: ${toDeg(cam.beta)}\u00B0 | Radius: ${(cam.radius || 50).toFixed(1)}m`, xOff, camY + imgH + 3);

          col++;
          if (col >= 2) { col = 0; y += imgH + 12; }
        }
      } catch (err) {
        console.warn('Camera capture failed:', err);
      }
    }
    if (col > 0) y += imgH + 12;
  }

  // ============ 6-ANGLE SCENE VIEWS (Ortho, dark background) ============
  if (scene && engine) {
    const bounds = computeSceneBoundsFromObjects(sceneObjects, projectors, scene);
    if (bounds) {
      pdf.addPage();
      y = m;
      hdr('Scene Views — 6 Angles');
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(90, 90, 90);
      pdf.text('Orthographic renders from 6 standard viewing angles — all scene objects visible.', m + 2, y);
      y += 5;

      const imgW6 = (cw - 6) / 2;
      let col6 = 0;

      for (const view of VIEWS_6) {
        try {
          const result = await captureOrthoScene(scene, engine, view, bounds, 1200, null);
          if (result?.data) {
            const jpgData = await toJpeg(result.data, 0.6);
            const imgH6 = imgW6 * Math.min(result.aspect, 2);
            if (col6 === 0) checkPage(imgH6 + 14);
            const xOff = col6 === 0 ? m : m + imgW6 + 6;
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(40, 40, 40);
            pdf.text(view.name + ' View', xOff, y);
            pdf.addImage(jpgData, 'JPEG', xOff, y + 2, imgW6, imgH6);
            col6++;
            if (col6 >= 2) { col6 = 0; y += imgH6 + 10; }
          }
        } catch (err) { console.warn('Scene view capture failed:', view.name, err); }
      }
      if (col6 > 0) y += 70;

      // ============ 6-SIDE LINE DRAWINGS ============ (removed per user request)
      // The "Orthographic Views — 6 Sides (Line Drawing)" page has been
      // intentionally disabled. Surface dimensions are still summarized below.
      const walls = sceneObjects.filter(o =>
        !['projector','reverse_projector','truss_system','scaffolding','led_screen','mirror'].includes(o.type)
      );
      const wallDimText = walls.map(w => {
        const d = w.dimensions || {};
        return `${w.name || w.type}: ${(d.width||0).toFixed(1)} x ${(d.height||0).toFixed(1)}m`;
      }).join('  |  ');

      if (wallDimText) {
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        const wallLines = pdf.splitTextToSize('Surfaces: ' + wallDimText, cw);
        wallLines.forEach(line => { checkPage(5); pdf.text(line, m, y); y += 3.5; });
        y += 2;
      }

      // ============ LUX HEATMAP VIEWS ============ (removed per user request)
      // The lux heatmap screenshot section has been intentionally disabled in
      // the projection report. Lux numbers are still reported per-projector in
      // the summary table above; only the visual heatmap captures are skipped.
    }
  }

  // ============ OVERLAP ANALYSIS ============
  const overlaps = calculateOverlaps ? calculateOverlaps() : overlapAnalysis;
  if (overlaps && overlaps.length > 0) {
    checkPage(30);
    hdr('Overlap Analysis');
    const ovhHeaders = ['Projector Pair', 'Overlap %', 'Size (m)', 'Resolution (px)'];
    const ovhWidths = [60, 30, 40, 40];
    const ovhRows = overlaps.map(ov => [
      `${ov.projector1} \u2194 ${ov.projector2}`,
      `${ov.overlapPercent}%`,
      `${ov.overlapSize.width} x ${ov.overlapSize.height}`,
      `${ov.overlapPixels.x} x ${ov.overlapPixels.y}`
    ]);
    drawTable(ovhHeaders, ovhRows, ovhWidths);
    sep(4);
  }

  // ============ GLOBAL SUMMARY ============
  pdf.addPage();
  y = m;
  pdf.setFillColor(18, 24, 38);
  pdf.rect(0, 0, pw, 18, 'F');
  pdf.setFillColor(200, 50, 200);
  pdf.rect(0, 18, pw, 1, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Global System Summary', m, 12);
  y = 24;

  // System overview
  hdr('System Overview');
  const totalLumens = projectors.reduce((s, p) => s + effectiveLumens(p), 0);
  const totalPixels = projectors.reduce((s, p) => s + ((p.image_width || 1920) * (p.image_height || 1200)), 0);
  const avgPPM = projectors.length > 0 ? projectors.reduce((s, p) => {
    const r = ASPECT_RATIOS[p.aspect_ratio] || ASPECT_RATIOS['16:10'];
    const w = (p.throw_distance || 5) / (p.throw_ratio || 1.5);
    return s + ((p.image_height || 1200) / (w / r.value));
  }, 0) / projectors.length : 0;

  row2col('Total Projectors', `${projectors.length}`, 'Total Lumens', `${totalLumens.toLocaleString()} lm`);
  row2col('Total Pixels', `${(totalPixels / 1000000).toFixed(2)} MP`, 'Avg Pixel Density', `${Math.round(avgPPM)} px/m`);
  row('Surfaces', `${surfaceGroups.length}`);
  sep(3);

  // Weight Breakdown
  hdr('Weight Breakdown');
  const wtHeaders = ['Projector', 'Model', 'Body (kg)', 'Frame (kg)', 'Clamp (kg)', 'Total (kg)'];
  const wtWidths = [24, 38, 24, 24, 24, 24];
  let grandWeight = 0;
  const wtRows = projectors.map(p => {
    const bw = p.weight || p.spec?.weight || 20;
    const fw = p.show_frame ? (p.frame_weight || p.spec?.frame_weight || 0) : 0;
    const cw2 = p.show_clamp ? (p.clamp_weight || 2) : 0;
    const tw = bw + fw + cw2;
    grandWeight += tw;
    return [
      (p.name || '').substring(0, 14),
      (p.model || '').substring(0, 22),
      bw.toFixed(1), fw > 0 ? fw.toFixed(1) : '—', cw2 > 0 ? cw2.toFixed(1) : '—', tw.toFixed(1)
    ];
  });
  wtRows.push([
    { text: 'TOTAL', _color: [200, 50, 200] }, '', '', '', '',
    { text: `${grandWeight.toFixed(1)} kg`, _color: [200, 50, 200] }
  ]);
  drawTable(wtHeaders, wtRows, wtWidths);
  sep(3);

  // Power Load
  hdr('Power Load');
  const pwHeaders = ['Projector', 'Model', 'Power (W)', 'Power (kW)', 'Current (A)'];
  const pwWidths = [24, 42, 28, 28, 32];
  let grandPower = 0;
  const pwRows = projectors.map(p => {
    const stkN = getStackCount(p);
    const pw2 = (p.spec?.power || estimatePowerW(p.lumens)) * stkN;
    grandPower += pw2;
    return [
      (p.name || '').substring(0, 14),
      (p.model || '').substring(0, 24),
      `${pw2}`, `${(pw2 / 1000).toFixed(2)}`, `${(pw2 / VOLTAGE).toFixed(1)} @ ${VOLTAGE}V`
    ];
  });
  pwRows.push([
    { text: 'TOTAL', _color: [200, 50, 200] }, '',
    { text: `${grandPower} W`, _color: [200, 50, 200] },
    { text: `${(grandPower / 1000).toFixed(2)} kW`, _color: [200, 50, 200] },
    { text: `${(grandPower / VOLTAGE).toFixed(1)} A`, _color: [200, 50, 200] }
  ]);
  drawTable(pwHeaders, pwRows, pwWidths);
  sep(3);

  // Resolution Per Surface
  const surfaces = sceneObjects.filter(o =>
    !['projector','reverse_projector','truss_system','scaffolding','led_screen','mirror'].includes(o.type)
  );
  if (surfaces.length > 0) {
    hdr('Total Resolution Per Surface');
    surfaces.forEach(obj => {
      checkPage(20);
      const dims2 = obj.dimensions || { width: 1, height: 1, depth: 1 };
      const hRes = obj.heightResolution || Math.round((dims2.height || 1) * PX_PER_METER);
      const ppm = hRes / (dims2.height || 1);
      const ot = (obj.type || '').toLowerCase();

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      pdf.text(`${obj.name || obj.type} (${dims2.width?.toFixed(1)} x ${dims2.height?.toFixed(1)} x ${(dims2.depth || 0).toFixed(1)} m)`, m + 2, y);
      y += 5;

      const faces = [];
      if (ot === 'cylinder' || ot === 'pipe') {
        const circ = 2 * Math.PI * (dims2.width || 1) / 2;
        faces.push({ name: 'Curved', w: Math.round(circ * ppm), h: hRes });
      } else if (ot === 'plane' || ot === 'projection_screen' || ot === 'scrim_screen') {
        faces.push({ name: 'Front', w: Math.round((dims2.width || 1) * ppm), h: hRes });
      } else if (ot === 'sphere' || ot === 'hemisphere' || ot === 'dome') {
        faces.push({ name: 'Spherical', w: Math.round((dims2.width || 1) * ppm) * 2, h: hRes });
      } else {
        const wP = Math.round((dims2.width || 1) * ppm);
        const dP = Math.round((dims2.depth || 1) * ppm);
        faces.push({ name: 'Front/Back', w: wP, h: hRes });
        faces.push({ name: 'Left/Right', w: dP, h: hRes });
        faces.push({ name: 'Top/Bottom', w: wP, h: dP });
      }

      const rfH = ['Face', 'Resolution'];
      const rfW2 = [32, 50];
      const rfR = faces.map(f => [f.name, `${f.w.toLocaleString()} x ${f.h.toLocaleString()} px`]);
      drawTable(rfH, rfR, rfW2);
      y += 2;
    });
  }

  // ============ PAGE NUMBERS ============
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Page ${i} of ${pageCount}`, pw / 2 - 10, ph - 5);
    pdf.text('AV Simulation Tool | Confidential', m, ph - 5);
  }

  const fileName = `${projectName || 'Projection'}_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(fileName);
  return fileName;
}
